import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from "react";
import { isDownlinkMessageType, abnormalCpdlcDialogStates, cancellableCpdlcDialogStates, deleteCpdlcDialogStates, EramCpdlcDialog, formatCpdlcMessage, } from "@poscon/eram-cpdlc";
import { formatUtcTime, TRIANGLE_DOWN_FILLED, TRIANGLE_DOWN_OUTLINE, TRIANGLE_UP_FILLED, TRIANGLE_UP_OUTLINE, } from "@poscon/shared-types";
import { useDispatch } from "react-redux";
import { eramFontNameMap, eramTextDimensionMap } from "../../eramFont.js";
import { MenuElement } from "./MenuElement.js";
import { ViewItem } from "./ViewItem.js";
import { colorNameMap } from "../../colorNameMap.js";
import { useScrollProps } from "../hooks/useScrollProps.js";
import { ScrollBar } from "./ScrollBar.js";
import { Line } from "./Line.js";
import { createItemOption, ViewItemOptionContainer } from "./ViewItemOptionContainer.js";
import { baseEramHubConnection } from "../../eramHubConnectionFactory.js";
import { errorTone } from "../../audio/errorTone.js";
import { useCursorContext } from "../contexts/cursorContext.js";
import { computeColor } from "../../computeColor.js";
import { cpdlcMsgOutView } from "./CpdlcMsgOut";
import { cpdlcMiniMOView } from "./CpdlcMiniMO";
import { selectedViewOptionSelector, setSelectedViewOption, toggleSelectedViewOption, } from "../slices/commonSlice";
import { useRootSelector } from "../stateSelectors";
import { useCommonContext } from "../contexts/commonContext.js";
import { cpdlcTocHeldView } from "./CpdlcTocHeld.js";
const statusSortOrder = [
    ["ICMSM", "ERR", "FAIL", "UNA", "UNA*", "NS", "NS*"],
    ["HELD", "HELD*"],
    ["TIM"],
    ["SBY"],
    ["SNT"],
    ["CNCL"],
    ["WIL", "ROG"],
    ["RCVD"],
];
function compareDialogAcid(a, b) {
    const acidA = a.fp.callsign;
    const acidB = b.fp.callsign;
    return acidA.localeCompare(acidB);
}
function compareDialogStat(a, b) {
    const aState = a.dialog?.eramState ?? (a.tocMsgs?.length === 1 ? "HELD" : "HELD*");
    const bState = b.dialog?.eramState ?? (b.tocMsgs?.length === 1 ? "HELD" : "HELD*");
    const aStatusIndex = statusSortOrder.findIndex((v) => v.includes(aState));
    const bStatusIndex = statusSortOrder.findIndex((v) => v.includes(bState));
    return aStatusIndex - bStatusIndex;
}
function compareDialogTime(a, b) {
    const aTime = a.dialog?.statusTime ?? 0;
    const bTime = b.dialog?.statusTime ?? 0;
    return aTime - bTime;
}
const dialogSortFuncMap = {
    ACID_ASC: (a, b) => compareDialogAcid(a, b),
    STAT_ASC: (a, b) => compareDialogStat(a, b),
    TIME_ASC: (a, b) => compareDialogTime(a, b),
    ACID_DESC: (a, b) => compareDialogAcid(b, a),
    TIME_DESC: (a, b) => compareDialogTime(b, a),
};
function compareDialogs(a, b, primaryMethod) {
    const secondaryMethod = primaryMethod.startsWith("ACID") ? "STAT_ASC" : "TIME_DESC";
    const primaryValue = dialogSortFuncMap[primaryMethod](a, b);
    const secondaryValue = dialogSortFuncMap[secondaryMethod](a, b);
    if (primaryValue === 0) {
        return secondaryValue === 0 ? dialogSortFuncMap.TIME_DESC(a, b) : secondaryValue;
    }
    return primaryValue;
}
function colDimensionFactory(fontSize, totalWidth) {
    const fontDim = eramTextDimensionMap[fontSize];
    const acidX = 4;
    const acidWidth = fontDim.width * 11 + 4;
    const msgX = acidX + acidWidth + 4;
    const msgWidth = totalWidth - fontDim.width * 32;
    const statX = msgX + msgWidth + 4;
    const statWidth = fontDim.width * 6 + 4;
    const timeX = statX + statWidth + 6;
    const timeWidth = fontDim.width * 6 + 4;
    const orgX = timeX + timeWidth + 4;
    const orgWidth = fontDim.width * 4 + 4;
    return {
        acidX,
        acidWidth,
        msgX,
        msgWidth,
        statX,
        statWidth,
        timeX,
        timeWidth,
        orgX,
        orgWidth,
    };
}
const msgOutViews = [cpdlcMsgOutView, cpdlcMiniMOView];
export function msgOutColOptionFactory(view, fp, dialog, deselect) {
    const flidOptions = [];
    if (msgOutViews.includes(view)) {
        if (abnormalCpdlcDialogStates.includes(dialog.eramState)) {
            if (!dialog.acknowledgedAt) {
                flidOptions.push(createItemOption("ACKNOWLEDGE", () => {
                    baseEramHubConnection.emit("acknowledgeCpdlcDialogStatus", fp.id, dialog.id, false);
                    deselect();
                }), createItemOption("ACKNOWLEDGE+DELETE", () => baseEramHubConnection.emit("acknowledgeCpdlcDialogStatus", fp.id, dialog.id, true)));
            }
            else {
                flidOptions.push(createItemOption("DELETE", () => {
                    baseEramHubConnection.deleteCdplcDialog(fp.id, dialog.id);
                    deselect();
                }));
            }
        }
        else if (deleteCpdlcDialogStates.includes(dialog.eramState) || EramCpdlcDialog.dialogIsClosed(dialog)) {
            flidOptions.push(createItemOption("DELETE", () => {
                baseEramHubConnection.deleteCdplcDialog(fp.id, dialog.id);
                deselect();
            }));
        }
        if (cancellableCpdlcDialogStates.includes(dialog.eramState) && dialog.status === "OPEN") {
            flidOptions.push(createItemOption("CANCEL UPLINK-VOICE REQD", () => {
                baseEramHubConnection.emit("cancelUplinkDialog", fp.id, dialog.id);
                deselect();
            }));
        }
    }
    const statusOptions = [...flidOptions];
    if (dialog.closureDueToReason) {
        statusOptions.unshift(createItemOption(dialog.closureDueToReason, undefined, { bgColor: 0 }));
    }
    return {
        flidOptions,
        statusOptions,
    };
}
export function heldTocOptions(fpId, deselect) {
    const flidOptions = [
        createItemOption("CLEANUP+RELEASE-VOICE REQD", () => {
            baseEramHubConnection.emit("cleanupTocAndRelease", fpId);
            deselect();
        })
    ];
    const statusOptions = [...flidOptions];
    return {
        flidOptions,
        statusOptions,
    };
}
export const getCpdlcMsgRowHeight = (fontSize) => eramTextDimensionMap[fontSize].height + 3;
const CpdlcMsgTableRow = ({ path, x = 0, y = 0, fontSize, alpha, view, fp, dialog, tocMsgs, totalWidth, openTocMenu, }) => {
    const dispatch = useDispatch();
    const ref = useRef(null);
    const { setViewPosition } = useCommonContext();
    const selectedViewOptionPath = useRootSelector(selectedViewOptionSelector);
    const colDim = colDimensionFactory(fontSize, totalWidth);
    const fontName = eramFontNameMap[fontSize];
    const handleItemClick = (path, xOffset = 0) => {
        if (tocMsgs && path.endsWith("msg")) {
            if (ref.current) {
                const pos = ref.current.getGlobalPosition();
                const x = pos.x + xOffset + 1;
                const y = pos.y;
                setViewPosition(cpdlcTocHeldView, { x, y });
            }
            openTocMenu?.(fp.id);
        }
        else {
            dispatch(toggleSelectedViewOption(path));
        }
    };
    const options = dialog
        ? msgOutColOptionFactory(cpdlcMiniMOView, fp, dialog, () => dispatch(setSelectedViewOption(null)))
        : tocMsgs ? heldTocOptions(fp.id, () => dispatch(setSelectedViewOption(null))) : null;
    let tint = computeColor(colorNameMap.lightGrey, alpha);
    if (view === cpdlcMsgOutView &&
        dialog &&
        (dialog.cancelledAt ||
            (EramCpdlcDialog.dialogIsClosed(dialog) &&
                (!abnormalCpdlcDialogStates.includes(dialog.eramState) || dialog.acknowledgedAt)))) {
        tint = computeColor(colorNameMap.mediumGrey, alpha);
    }
    const statusTint = view === cpdlcMsgOutView &&
        dialog &&
        abnormalCpdlcDialogStates.includes(dialog.eramState) &&
        !dialog.acknowledgedAt
        ? computeColor(colorNameMap.orange, alpha)
        : tint;
    const descr = dialog || tocMsgs?.length === 1 ? formatCpdlcMessage(dialog?.messages[0] ?? tocMsgs[0]) : null;
    const description = descr?.description ?? "";
    const maxDescriptionLength = Math.floor(colDim.msgWidth / eramTextDimensionMap[fontSize].width);
    const descriptionToShow = description.length > maxDescriptionLength
        ? `${description.slice(0, maxDescriptionLength - 2)}*`
        : description;
    const status = dialog?.eramState ?? (tocMsgs.length === 1 ? "HELD" : "HELD*");
    const basePath = `${path}/row/${fp.id}` + (dialog ? `/dialog/${dialog.id}` : `/toc`) + `/${status}`;
    let time = dialog?.statusTime ? formatUtcTime(dialog?.statusTime) : "N/A";
    if (dialog?.messages[0]?.elements[0] && isDownlinkMessageType(dialog.messages[0].elements[0].type)) {
        time += ` ${TRIANGLE_DOWN_FILLED}`;
    }
    return (_jsxs("container", { x: x, y: y, ref: ref, eventMode: "static", sortableChildren: true, zIndex: selectedViewOptionPath?.startsWith(basePath) ? 2 : 1, children: [_jsx(ViewItem, { x: colDim.acidX, eventMode: options?.flidOptions?.length || tocMsgs?.length ? "static" : "none", width: colDim.acidWidth, centerText: false, text: `${fp.cid} ${fp.callsign}`, onmousedown: () => handleItemClick(`${basePath}/flid`, colDim.acidX + colDim.acidWidth), fontName: fontName, fill: true, fillColor: selectedViewOptionPath === `${basePath}/flid` ? tint : 0, tint: selectedViewOptionPath === `${basePath}/flid` ? 0 : tint }), options && selectedViewOptionPath === `${basePath}/flid` && (_jsx(ViewItemOptionContainer, { baseX: colDim.acidX, xOffset: colDim.acidWidth, options: options.flidOptions })), _jsx(ViewItem, { x: colDim.msgX, eventMode: descr?.longDescription ? "static" : "none", onmousedown: () => handleItemClick(`${basePath}/msg`, colDim.msgX + descriptionToShow.length * eramTextDimensionMap[fontSize].width + 4), text: descriptionToShow, fontName: fontName, fill: true, fillColor: selectedViewOptionPath === `${basePath}/msg` ? tint : 0, tint: selectedViewOptionPath === `${basePath}/msg` ? 0 : tint }), descr && selectedViewOptionPath === `${basePath}/msg` && (_jsx(ViewItemOptionContainer, { baseX: colDim.msgX, xOffset: descriptionToShow.length * eramTextDimensionMap[fontSize].width + 4, maxRowWidth: eramTextDimensionMap[2].width * 40 + 4, options: [createItemOption(descr.longDescription)] })), _jsx(ViewItem, { x: colDim.statX, eventMode: (options && options.statusOptions.length > 0) || tocMsgs?.length ? "static" : "none", onmousedown: () => handleItemClick(`${basePath}/status`, colDim.statX + status.length * eramTextDimensionMap[fontSize].width + 4), text: status, fontName: fontName, fill: true, fillColor: selectedViewOptionPath === `${basePath}/status` ? statusTint : 0, tint: selectedViewOptionPath === `${basePath}/status` ? 0 : statusTint }), options && selectedViewOptionPath === `${basePath}/status` && (_jsx(ViewItemOptionContainer, { baseX: colDim.statX, xOffset: status.length * eramTextDimensionMap[fontSize].width + 4, options: options.statusOptions })), _jsx(ViewItem, { x: colDim.timeX, text: time, eventMode: "none", fontName: fontName, tint: tint }), dialog?.eligiblePosition?.positionType && (_jsx(ViewItem, { x: colDim.orgX, fontName: fontName, text: `${dialog.eligiblePosition.positionType}${dialog.eligiblePosition.sectorId.split(".").at(-1).slice(0, 3)}`, tint: tint, eventMode: "none" }))] }));
};
export const CpdlcMsgTable = ({ path = "0", x = 0, y = 0, fontSize = 2, alpha, fpDialogs, totalWidth, maxRows = 10, view, openTocMenu, noSort = false, }) => {
    const { setCursorOverride } = useCursorContext();
    const scrollProps = useScrollProps(`${view}/${path}/cpdlcMsgTable`, fpDialogs.length, maxRows, {
        scrollDelta: maxRows,
    });
    const [sortMethod, setSortMethod] = useState("STAT_ASC");
    const sortedFpDialogs = fpDialogs.slice().sort((a, b) => compareDialogs(a, b, sortMethod));
    const fontName = eramFontNameMap[fontSize];
    const fontDim = eramTextDimensionMap[fontSize];
    const colDim = colDimensionFactory(fontSize, totalWidth);
    const dialogsToRender = sortedFpDialogs.slice(scrollProps.scroll, scrollProps.scroll + maxRows);
    const rowHeight = getCpdlcMsgRowHeight(fontSize);
    const rows = Math.min(fpDialogs.length, maxRows);
    const listHeight = rowHeight * rows + 3 * Math.floor(rows / 3);
    let tableY = 0;
    const acidSortIcon = sortMethod === "ACID_ASC"
        ? TRIANGLE_UP_FILLED
        : sortMethod === "ACID_DESC"
            ? TRIANGLE_DOWN_FILLED
            : TRIANGLE_UP_OUTLINE;
    const timeSortIcon = sortMethod === "TIME_ASC"
        ? TRIANGLE_UP_FILLED
        : sortMethod === "TIME_DESC"
            ? TRIANGLE_DOWN_FILLED
            : TRIANGLE_DOWN_OUTLINE;
    const tint = computeColor(colorNameMap.lightGrey, alpha);
    return (_jsxs("container", { x: x, y: y, zIndex: 2, sortableChildren: true, children: [_jsx(MenuElement, { x: colDim.acidX, textX: 2, width: colDim.acidWidth, text: `CID ACID${noSort ? "" : acidSortIcon}`, fontName: fontName, tint: tint, fillColor: 0, borderColor: 0, disabled: noSort, onmousedown: () => {
                    setSortMethod((prev) => (prev === "ACID_ASC" ? "ACID_DESC" : "ACID_ASC"));
                } }), _jsx(MenuElement, { x: colDim.msgX, text: "MSG CONTENT", fontName: fontName, tint: tint, fillColor: 0, borderColor: 0, disabled: true }), _jsx(MenuElement, { x: colDim.statX, textX: 2, width: colDim.statWidth, text: `STAT${noSort ? "" : sortMethod === "STAT_ASC" ? TRIANGLE_UP_FILLED : TRIANGLE_UP_OUTLINE}`, fontName: fontName, tint: tint, fillColor: 0, borderColor: 0, disabled: noSort, onmousedown: () => {
                    if (sortMethod === "STAT_ASC") {
                        errorTone.tryPlay();
                        setCursorOverride("InvalidSelectionX");
                    }
                    setSortMethod("STAT_ASC");
                } }), _jsx(MenuElement, { x: colDim.timeX, textX: 2, width: colDim.timeWidth, text: `TIME${noSort ? "" : timeSortIcon}`, fontName: fontName, tint: tint, fillColor: 0, borderColor: 0, disabled: noSort, onmousedown: () => {
                    setSortMethod((prev) => (prev === "TIME_ASC" ? "TIME_DESC" : "TIME_ASC"));
                } }), _jsx(MenuElement, { x: colDim.orgX, textX: 2, width: colDim.orgWidth, text: "ORG", fontName: fontName, tint: tint, fillColor: 0, borderColor: 0 }), _jsx(Line, { y: fontDim.height + 2, length: totalWidth - 1, tint: 0x303030 }), _jsxs("container", { y: fontDim.height + 4, sortableChildren: true, zIndex: 2, children: [dialogsToRender.map((v, i) => {
                        const drawSep = i % 3 === 2 && i < dialogsToRender.length - 1;
                        const _y = tableY;
                        tableY += rowHeight + (drawSep ? 3 : 0);
                        const key = v.dialog ? `dialog${v.dialog.id}` : `toc${v.tocMsgs.at(0).id}`;
                        return (_jsxs(React.Fragment, { children: [_jsx(CpdlcMsgTableRow, { path: path, y: _y, fontSize: fontSize, alpha: alpha, totalWidth: totalWidth, view: view, openTocMenu: openTocMenu, ...v }), drawSep && (_jsx(Line, { x: 4, y: _y + rowHeight + 1, length: totalWidth - fontDim.width - 10, tint: 0x303030 }))] }, key));
                    }), maxRows < fpDialogs.length && (_jsx(ScrollBar, { x: totalWidth - (fontDim.width + 3), y: 0, height: listHeight - 4, fontSize: fontSize, ...scrollProps }))] })] }));
};
//# sourceMappingURL=CpdlcMsgTable.js.map